/**
 * Responsibility: hold the registry of selectable aircraft.
 *
 * Plain data — vertex and edge lists, no Three.js. Adding an aircraft means importing it here and
 * adding it to the list; nothing else changes, because the flight model and the chase camera never
 * learn which one is selected. That independence is what makes swapping instant and safe mid-flight:
 * there is no state to migrate, only a mesh to replace.
 *
 * ## Order matters
 *
 * The list is the cycle order the player steps through, and the first entry is the default. The
 * invisible model is last on purpose — it should be somewhere you arrive deliberately, not the second
 * thing you land on while looking for a shape you like.
 */

import { defaultAircraft } from './default.js';
import { needleAircraft } from './needle.js';
import { ringAircraft } from './ring.js';
import { invisibleAircraft } from './invisible.js';

/** Every aircraft, in cycle order. The first is the default. */
export const AIRCRAFT = [defaultAircraft, needleAircraft, ringAircraft, invisibleAircraft];

/** Id used when there is no save and the player has not chosen. */
export const DEFAULT_AIRCRAFT_ID = AIRCRAFT[0].id;

/**
 * Look up an aircraft by id.
 *
 * @param {string} id
 * @returns {object} The aircraft descriptor.
 * @throws {Error} If no aircraft has that id. A save naming a model that no longer exists is a real
 *   possibility, so callers that load untrusted ids should use `getAircraftOrDefault`.
 */
export function getAircraft(id) {
  const aircraft = AIRCRAFT.find((candidate) => candidate.id === id);
  if (!aircraft) {
    throw new Error(`getAircraft: no aircraft registered with id "${id}"`);
  }
  return aircraft;
}

/**
 * Look up an aircraft by id, falling back to the default.
 *
 * For loading saves and settings, where an id may name a model that has since been renamed or
 * removed. Per the Error Boundary Rule, bad persisted data degrades rather than throwing.
 *
 * @param {string} id
 * @returns {object}
 */
export function getAircraftOrDefault(id) {
  return AIRCRAFT.find((candidate) => candidate.id === id) ?? AIRCRAFT[0];
}

/**
 * The aircraft after this one in the cycle, wrapping at the end.
 *
 * @param {string} id - Current aircraft id. An unknown id starts the cycle from the beginning.
 * @returns {object}
 */
export function nextAircraft(id) {
  const index = AIRCRAFT.findIndex((candidate) => candidate.id === id);
  return AIRCRAFT[(index + 1) % AIRCRAFT.length];
}

/**
 * Validate that a descriptor has everything the render layer expects.
 *
 * Called by tests and by the registry check below, so a malformed aircraft fails at startup rather
 * than producing a mesh full of NaNs that silently renders nothing.
 *
 * @param {object} aircraft
 * @returns {boolean}
 */
export function isValidAircraft(aircraft) {
  if (
    !aircraft ||
    typeof aircraft.id !== 'string' ||
    aircraft.id.length === 0 ||
    typeof aircraft.label !== 'string' ||
    aircraft.label.length === 0 ||
    !Array.isArray(aircraft.vertices) ||
    aircraft.vertices.length === 0 ||
    !Array.isArray(aircraft.edges)
  ) {
    return false;
  }

  for (const vertex of aircraft.vertices) {
    if (!Array.isArray(vertex) || vertex.length !== 3) return false;
    if (!vertex.every((component) => Number.isFinite(component))) return false;
  }

  for (const edge of aircraft.edges) {
    if (!Array.isArray(edge) || edge.length !== 2) return false;

    for (const index of edge) {
      // An out-of-range index would write NaNs and blank the whole mesh, not just one edge.
      if (!Number.isInteger(index) || index < 0 || index >= aircraft.vertices.length) return false;
    }

    // A self-referencing edge is a zero-length segment: invisible, and a sign of a typo.
    if (edge[0] === edge[1]) return false;
  }

  return true;
}

// Fail fast on a malformed or duplicated registry entry.
for (const aircraft of AIRCRAFT) {
  if (!isValidAircraft(aircraft)) {
    throw new Error(`aircraft/index: invalid aircraft descriptor "${aircraft?.id ?? 'unknown'}"`);
  }
}
if (new Set(AIRCRAFT.map((a) => a.id)).size !== AIRCRAFT.length) {
  throw new Error('aircraft/index: duplicate aircraft id in registry');
}
